import React, { useState } from 'react';
import { Card, Button } from 'react-bootstrap';
import { MdRefresh } from 'react-icons/md';
import moment from 'moment';
import VisitorListItem from './VisitorListItem';
import LoadingOverlay from '../../common/LoadingOverlay';
import Pagination from '../../common/Pagination';
import { useTranslation } from 'react-i18next';
import handleClientSideSearch from '../../../utils/handleClientSideSearch';
import handleClientSideSorting from '../../../utils/handleClientSideSorting';
import exportAsCsv from '../../../utils/exportAsCsv';
import usePagination from '../../../hooks/usePagination';
import './VisitorsList.scss';

function VisitorsList({ eventID, loading, visitors, refresh }) {
    const { t } = useTranslation();
    const [limit, setLimit] = useState(25);
    const [search, setSearch] = useState(null);
    const [sorter, setSorter] = useState({ column: 'timeCreated', direction: 'desc' });

    let filteredVisitors = [...visitors];

    if (search) {
		filteredVisitors = handleClientSideSearch(filteredVisitors, search);
    }
    
    if (sorter) {
        filteredVisitors = handleClientSideSorting(filteredVisitors, sorter);
    }

    const hasTimeLatestActivity = visitors.findIndex(v => v.timeLatestActivity) > -1;
    const hasActivityTime = visitors.findIndex(v => v.activityTime) > - 1;

    const sortingOptions = [
        { value: '{ "column": "firstName", "direction": "asc" }', text: `${t('general.first_name')} (A-Z)`},
        { value: '{ "column": "firstName", "direction": "desc" }', text: `${t('general.first_name')} (Z-A)`},
        { value: '{ "column": "lastName", "direction": "asc" }', text: `${t('general.last_name')} (A-Z)`},
        { value: '{ "column": "lastName", "direction": "desc" }', text: `${t('general.last_name')} (Z-A)`},
        { value: '{ "column": "company", "direction": "asc" }', text: `${t('general.company')} (A-Z)`},
        { value: '{ "column": "company", "direction": "desc" }', text: `${t('general.company')} (Z-A)`},
        { value: '{ "column": "timeCreated", "direction": "asc" }', text: `${t('analytics.first_seen')} ↑`},
        { value: '{ "column": "timeCreated", "direction": "desc" }', text: `${t('analytics.first_seen')} ↓`},
        ...(hasTimeLatestActivity ? [
            { value: '{ "column": "timeLatestActivity", "direction": "asc" }', text: `${t('analytics.latest_activity')} ↑`},
            { value: '{ "column": "timeLatestActivity", "direction": "desc" }', text: `${t('analytics.latest_activity')} ↓`},
        ]
        : []),
        ...(hasActivityTime ? [
            { value: '{ "column": "activityTime", "direction": "asc" }', text: `${t('analytics.activity_time')} ↑`},
            { value: '{ "column": "activityTime", "direction": "desc" }', text: `${t('analytics.activity_time')} ↓`},
        ] : [])
    ];

    const _handleExportVisitors = (fileType) => {
        const csvData = [...visitors];
        let columns = [ 
            {key: 'firstName', label: t('general.first_name')},
            {key: 'lastName', label: t('general.last_name')},
            ...(eventID !== '0c01107f-e93d-11ea-a21a-ee6a04371b85' ? [
                {key: 'email', label: t('general.email')},
                {key: 'phone', label: t('general.phone_number')},
            ]: []),
            {key: 'company', label: t('general.company')},
            {key: 'jobTitle', label: t('general.job_title')},
            {key: 'timeCreated', label: t('analytics.first_seen')},
            ...(hasTimeLatestActivity ? [{key: 'timeLatestActivity', label : t('analytics.latest_activity')}] : []),
            ...(hasActivityTime ? [{key: 'activityTime', label: t('analytics.activity_time'), customFormat: (item) => item ? moment().startOf('day').seconds(item).format('HH:mm:ss') : '' }] : [])
        ];
        exportAsCsv(csvData, 'Visitors', columns);
    }

    const _handlePageSize = ({ target }) => setLimit(Number(target.value));
    const _handleSearch = ({ target }) => setSearch(target.value || null);
    const _handleSorting = ({ target }) => setSorter(JSON.parse(target.value) || null);
    const _paginate = ({ selected }) => setPage(selected + 1);

    const {
		setPage,
		pageCount,
		currentPage,
		currentData,
    } = usePagination(filteredVisitors, limit);
    
    const currentVisitors = currentData();

    return (
        <Card className="mt-0">
            <Card.Header>
                {t('analytics.browse_visitors')} ({visitors.length})
                <Card.Subtitle className="mt-1">
                    <p className="text-muted">{t('analytics.browse_visitors_text')}</p>
                </Card.Subtitle>
            </Card.Header>

            <div className="container DataListInputs">
                <div className="row justify-content align-items-center">
                    <div className="col col-sm-auto">
                        <select
                            className="form-control form-control-sm"
                            defaultValue={25}
                            onChange={_handlePageSize}
                        >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div className="col col-sm-auto">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('general.search')}
                            onChange={_handleSearch}
                        />
                    </div>
                    <div className="col col-sm-auto">
                        <select
                            className="form-control form-control-sm"
                            defaultValue='{ "column": "timeCreated", "direction": "desc" }'
                            onChange={_handleSorting}
                        >
                            {sortingOptions.map((o, i) => (
                                <option
                                    key={i}
                                    value={o.value}
                                >
                                    {o.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col text-right">
                        <Button
                            size="sm"
                            className="mr-3"
                            onClick={refresh}
                        >
                            <MdRefresh size={18} />
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => _handleExportVisitors('csv')}
                            disabled={!visitors.length}
                        >
                            {t('general.download_report')}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="DataList">
                {currentVisitors.map((visitor, i) => (
                    <VisitorListItem
                        key={visitor.email || i}
                        visitor={visitor}
                        eventID={eventID}
                    />
                ))}
                {loading ?
                    <LoadingOverlay />
                : null}
            </div>

            {visitors.length ?
                <Card.Footer>
                    <div className="pull-right">
                        <Pagination 
                            forcePage={currentPage}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={_paginate}
                        />
                    </div>
                </Card.Footer>
            : null}

            {!visitors.length ?
                <Card.Body>
                    <>{t('analytics.no_visitors')}</>
                </Card.Body>
            : null}
        </Card>
    );
}

export default VisitorsList;